import axios from 'axios'

import type {
  DescriptionSyncJobFailure,
  DescriptionSyncJobFailureKind,
  DescriptionSyncJobResultsPage,
  DescriptionSyncJobStatus,
  StartDescriptionSyncJobResponse,
} from './descriptionSyncJob'
import { useAccountSettingsStore } from './stores/accountSettings'
import { getDescriptionsApi } from './utils'

export type DescriptionSyncJobApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; failure: DescriptionSyncJobFailure }

const authHeaders = (): Record<string, string> => {
  try {
    const token = String(useAccountSettingsStore()?.sessionToken || '').trim()
    return token ? { Authorization: `Bearer ${token}` } : {}
  } catch {
    return {}
  }
}

const KIND_BY_STATUS: Record<number, DescriptionSyncJobFailureKind> = {
  401: 'unauthenticated',
  403: 'forbidden',
  404: 'not-found',
  409: 'conflict',
}

const readJobId = (data: unknown): string | undefined => {
  const jobId = (data as { jobId?: unknown })?.jobId
  return typeof jobId === 'string' && jobId.trim() ? jobId : undefined
}

const toFailure = (error: unknown): DescriptionSyncJobFailure => {
  const response = axios.isAxiosError(error) ? error.response : undefined
  const kind = KIND_BY_STATUS[response?.status ?? 0] ?? 'unavailable'
  return kind === 'conflict' ? { kind, jobId: readJobId(response?.data) } : { kind }
}

const endpointFor = (path: string): string => `${getDescriptionsApi()}/${path}`

export const startDescriptionSyncJob = async (
  accountId: string,
): Promise<DescriptionSyncJobApiResult<StartDescriptionSyncJobResponse>> => {
  try {
    const { data } = await axios.post<StartDescriptionSyncJobResponse>(
      endpointFor('description-sync-jobs'),
      { accountId },
      { headers: { ...authHeaders() } },
    )
    return { ok: true, data }
  } catch (error) {
    return { ok: false, failure: toFailure(error) }
  }
}

export const fetchDescriptionSyncJobStatus = async (
  accountId: string,
  jobId: string,
): Promise<DescriptionSyncJobApiResult<DescriptionSyncJobStatus>> => {
  try {
    const { data } = await axios.get<DescriptionSyncJobStatus>(
      endpointFor(`description-sync-jobs/${encodeURIComponent(jobId)}`),
      { params: { accountId }, headers: { ...authHeaders() } },
    )
    return { ok: true, data }
  } catch (error) {
    return { ok: false, failure: toFailure(error) }
  }
}

export const fetchDescriptionSyncJobResults = async (
  accountId: string,
  jobId: string,
  page: number,
): Promise<DescriptionSyncJobApiResult<DescriptionSyncJobResultsPage>> => {
  try {
    const { data } = await axios.get<DescriptionSyncJobResultsPage>(
      endpointFor(`description-sync-jobs/${encodeURIComponent(jobId)}/results`),
      { params: { accountId, page }, headers: { ...authHeaders() } },
    )
    return { ok: true, data }
  } catch (error) {
    return { ok: false, failure: toFailure(error) }
  }
}
